import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createCardToken } from '@mercadopago/sdk-react';
import { findDOMNode } from 'react-dom';

/**
 * Encapsulates all payment validation and submission logic:
 * - Form validation (card, billing, phone number)
 * - Card tokenization with MP SDK
 * - Dispatch of createPayment / getMpInitPointRequest depending on method
 */
const useMakePayment = ({
  campaign,
  creditCardDetails,
  paymentMethodSelected,
  billingInformation,
  phoneGeneral,
  isSuperUser,
  createPayment,
  getMpInitPointRequest,
  createCampaignComment,
  campaignComment,
  billingInfDniRef
}) => {
  const { t } = useTranslation();
  const [creatingToken, setCreatingToken] = useState(false);
  const [errors, setErrors] = useState({});

  const handleErrorFocus = errorKey => {
    const refMap = { billingDni: billingInfDniRef };
    const ref = refMap[errorKey];
    if (ref && ref.current) {
      const domNode = findDOMNode(ref.current);
      if (domNode) domNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const validate = () => {
    const errs = {};
    const payWithCard = paymentMethodSelected === 'card';
    const isExternalDsp = campaign.external_dsp_id != null && !!campaign.smart_campaign;

    if (paymentMethodSelected === 'other' && !isExternalDsp && !phoneGeneral) {
      errs['phone_general'] = t('Can not be blank');
    }

    if (payWithCard) {
      if (!creditCardDetails.number) errs['creditCardNumber'] = t('Can not be blank');
      if (!creditCardDetails.name) errs['name'] = t('Can not be blank');
      if (!creditCardDetails.expiry)
        errs['expiry'] = t('The month / year') + ' ' + t('Can not be blank');
      if (!creditCardDetails.cvc) errs['cvc'] = t('Can not be blank');
      if (!creditCardDetails.documentType) errs['documentType'] = t('Can not be blank');
      if (!creditCardDetails.doc_number) {
        errs['doc_number'] = t('Can not be blank');
        handleErrorFocus('billingDni');
      }
    }

    if (!billingInformation.doc_number) {
      errs['doc_number'] = t('Can not be blank');
      handleErrorFocus('billingDni');
    }

    return errs;
  };

  const makePayment = () => {
    const payWithCard = paymentMethodSelected === 'card';
    const errs = validate();

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return false;
    }

    setErrors({});

    if (campaignComment && campaign && campaign.id) {
      createCampaignComment(campaign.id, campaignComment);
    }

    setCreatingToken(true);

    const publinetMethods = ['cash', 'cheque', 'transfer', 'credit_account'];
    const paymentByDsp =
      paymentMethodSelected === 'transfer' ? 'transfer' : creditCardDetails.paymentMethodId;
    const payWithPublinet = publinetMethods.includes(paymentByDsp);

    if (!payWithCard) {
      if (paymentMethodSelected === 'other') {
        createPayment(campaign.id, {
          payment_method_id: paymentByDsp,
          gateway: payWithPublinet ? 'publinet' : 'mercadopago_custom',
          payer_phone: phoneGeneral,
          billing_information: billingInformation
        });
      }
      if (paymentMethodSelected === 'mp') {
        getMpInitPointRequest(campaign.id, { billing_information: billingInformation });
      }
      setCreatingToken(false);
      return true;
    }

    // Card payment — tokenize first
    const month = creditCardDetails.expiry.split('/')[0];
    const year = `20${creditCardDetails.expiry.split('/')[1]}`;

    createCardToken(
      {
        cardNumber: creditCardDetails.number.replace(/\s/gi, ''),
        securityCode: creditCardDetails.cvc,
        cardExpirationMonth: month,
        cardExpirationYear: year,
        cardholderName: creditCardDetails.name,
        docType: creditCardDetails.documentType,
        docNumber: creditCardDetails.doc_number,
        installments: 1
      },
      (status, response) => {
        if (status === 200) {
          createPayment(campaign.id, {
            payment_method_id: creditCardDetails.paymentMethodId,
            token: response.id,
            installments: creditCardDetails.installments,
            gateway: 'mercadopago_custom',
            billing_information: billingInformation
          });
          setCreatingToken(false);
          setErrors({});
          return;
        }

        // MP error mapping
        const mpErrorMap = {
          '205': ['creditCardNumber', t('Can not be blank')],
          '208': ['expiry', t('The month / year') + ' ' + t('Can not be blank')],
          '214': ['doc_number', t('Can not be blank')],
          '221': ['name', t('Can not be blank')],
          '325': ['expiry', t('The month / year') + ' ' + t('Its not valid')],
          '326': ['expiry', t('The month / year') + ' ' + t('Its not valid')],
          E301: ['creditCardNumber', t('Its not valid')],
          E302: ['cvc', t('Its not valid')],
          '212': ['documentType', t('Its not valid')],
          '324': ['doc_number', t('Its not valid')]
        };

        const newErrors = {};
        if (response.cause && response.cause.length > 0) {
          response.cause.forEach(e => {
            if (mpErrorMap[e.code]) {
              const [field, msg] = mpErrorMap[e.code];
              newErrors[field] = msg;
            }
          });
        }

        setCreatingToken(false);
        setErrors(newErrors);
      }
    );

    return true;
  };

  return { makePayment, creatingToken, errors, setErrors };
};

export default useMakePayment;
